<style lang="scss">
  @use "../../../../../assets/css/global.scss" as *;
  @use "../../../../../assets/css/_colors.scss" as *;
  button {
    border: 0;
    background: none;
    text-align: inherit;
    width: 100%;
    color: inherit;
  }

  .todoItem {
    cursor: pointer;
    align-items: center;
    display: flex;
    padding: 0.8rem 1.2rem;
    gap: 1rem;
    border-radius: 0.5rem;

    &:hover {
      background-color: $secondary-hover-background-color;
    }

    &:has(input[type="checkbox"]:checked) {
      background-color: $light-blue;
    }

    .contentWrapper {
      align-items: center;
      display: flex;
      flex-grow: 1;
      gap: 1rem;
      justify-content: space-between;

      &.grayscale {
        pointer-events: none;
        filter: grayscale(100%);
        opacity: 0.7;
      }

      .textContainer {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        gap: 0.25rem;

        #overdue {
          width: fit-content;
          align-items: center;
          background-color: $light-red;
          border-radius: 5rem;
          color: $red;
          display: inline-flex;
          gap: 0.25rem;
          margin-top: 0.25rem;
          padding: 0.2rem 0.5rem;
          @extend .text-title-small;
        }

        #title {
          @extend .text-body-2-bold;

          a {
            color: $primary-color;

            &:hover {
              color: $blue;
            }
          }
        }

        .campaign-name {
          @extend .text-body-3;
          width: fit-content;
        }
      }

      .ctaContainer {
        width: 8rem;
        align-items: center;
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        gap: 0.625rem;

        #ctaButton {
          text-align: center;
          width: 100%;
          background-color: $primary-color;
          border: 0;
          border-radius: 5rem;
          color: white;
          cursor: pointer;
          font-family: inherit;
          padding: 0.65rem;
          @extend .text-body-2-bold;
          line-height: 1;

          &:hover {
            background-color: $blue;
          }

          &:disabled {
            opacity: 0.7;
            filter: grayscale(100%);
            cursor: default;
          }
        }
      }
    }
  }
</style>

<script>
  import { showToast } from "@sourcewhale/shared/src/assets/js/src/general/utils";
  import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
  import { createEventDispatcher } from "svelte";
  import { openExtensionFromTodo } from "../../../../utils/extension.js";
  import IconContainer from "../../../components/legacy/IconContainer.svelte";
  import CandidateLink from "../CandidateLink.svelte";
  import { openTodosModal } from "../stores";
  import { checkedToDos, preventToDosChecking } from "../utils/stores";
  import Checkbox from "./Checkbox.svelte";

  export let todo = {};
  export let ctaInactive = false;

  const dispatch = createEventDispatcher();
  let isChecked = false;

  const openLinkedIn = () => {
    let { url } = todo.data;
    if (!url) {
      showToast({
        title: `Could not find a URL for ${todo.candidateName}`,
        type: "danger",
      });
      return;
    }
    if (!url.startsWith("http")) url = `https://${url}`;

    dispatch("update-next-dashboard-to-dos", todo);

    openExtensionFromTodo({
      url,
      extensionId: $userStore.extensionId,
      todoExtensionVersion: $userStore.todoExtensionVersion,
    });
  };
  const mapping = {
    linkedinConnectionRequest: {
      title: "Send Connection Request to",
      displayCandidateName: true,
      icon: "liConnectionRequests",
      cta: "Go to LinkedIn",
      modal: "CandidateModal",
      callback: () => openLinkedIn(),
    },
    phoneCall: {
      title: "Call",
      displayCandidateName: true,
      icon: "calls",
      cta: "Call",
      modal: "todoModal",
      callback: () => openTodosModal.set(todo),
    },
    sendInmail: {
      title: "Send InMail to",
      displayCandidateName: true,
      icon: "liInMails",
      cta: "Go to LinkedIn",
      modal: "CandidateModal",
      callback: () => openLinkedIn(),
    },
    generalTask: {
      title: "General Task for",
      displayCandidateName: true,
      icon: "generalTasks",
      cta: "Go",
      modal: "todoModal",
      callback: () => openTodosModal.set(todo),
    },
    sendEmailAfterInmail: {
      title: "Approve Email to",
      displayCandidateName: true,
      icon: "approveEmails",
      cta: "Go",
      modal: "todoModal",
      callback: () => openTodosModal.set(todo),
    },
  };

  $: mappedToDo = mapping[todo.kind] || {};
  $: title = mappedToDo.title || "";
  $: icon = mappedToDo.icon || "";
  $: cta = mappedToDo.cta || "";

  const handleCtaClick = () => mappedToDo.callback();

  const openContactModal = () => {
    openTodosModal.set(todo, true);
    // contactModalOnly is true here
  };

  const addCheckedToDo = () => {
    if (!$checkedToDos.some((t) => t.toDoId === todo.toDoId)) {
      checkedToDos.update((todos) => [...todos, todo]);
    }
  };

  const removedUncheckedToDo = () => {
    if (!$checkedToDos.length) {
      return;
    }

    checkedToDos.update((todos) =>
      todos.filter((t) => t.toDoId !== todo.toDoId)
    );
  };

  const handleCheckboxChange = (checked) => {
    if ($preventToDosChecking) return;
    isChecked = checked;
    if (isChecked) {
      addCheckedToDo();
    } else if ($checkedToDos.length) {
      removedUncheckedToDo();
    }
  };

  const handleContentWrapperClick = (event) => {
    if (event.target.closest(".candidate-name, #ctaButton, a")) return;
    handleCheckboxChange(!isChecked);
  };

  const handleContentWrapperKeydown = (event) => {
    if (!["Enter", " "].includes(event.key)) return;
    event.preventDefault();
    handleContentWrapperClick(event);
  };

  $: {
    if (isChecked && !$checkedToDos.length) isChecked = false;
  }
</script>

<div
  id={todo.toDoId}
  class="todoItem"
  on:click={handleContentWrapperClick}
  on:keydown={handleContentWrapperKeydown}
  role="button"
  tabindex="0"
>
  <Checkbox
    callback={handleCheckboxChange}
    checked={isChecked}
    inactive={$preventToDosChecking}
  />
  <div class="contentWrapper" class:grayscale={isChecked}>
    <IconContainer {icon} size="md" />
    <div class="textContainer">
      {#if todo.isOverdue}
        <div id="overdue">
          <i class="fa-regular fa-clock-eight"></i>
          {todo.daysOverdue}
          {todo.daysOverdue === 1 ? "day" : "days"} Overdue
        </div>
      {/if}
      <span id="title"
        >{title}
        {#if mappedToDo?.displayCandidateName}
          <CandidateLink
            candidateName={todo.candidateName}
            callback={openContactModal}
          />
        {/if}
      </span>
      <a class="campaign-name" href="/campaign?campaignId={todo.campaignId}">
        {todo.campaignName}
      </a>
    </div>
    <div class="ctaContainer">
      <button id="ctaButton" disabled={ctaInactive} on:click={handleCtaClick}
        ><span class="resize">{cta}</span></button
      >
    </div>
  </div>
</div>
